import {
  type ChildId,
  type ChildWithProfile,
  DEFAULT_LOCALE,
  type Language,
  languageForLocale,
} from '@kidshub/contracts';

export interface AudioLanguageChoices {
  /** What to download in when the parent does not choose. */
  preferred: Language;
  /** Native languages first, then foreign ones; each once. */
  options: Language[];
  /**
   * The audience's native languages, so a choice can say which is which. The
   * default-locale fallback for a family with no children is not one of them.
   */
  natives: Language[];
}

/**
 * Which audio languages an import offers, and which it starts on.
 *
 * Read from the children the videos are for, because the language is theirs:
 * a parent who never thinks about it gets the first child's native language,
 * which is what that child can follow. When nobody is chosen yet, the whole
 * family is the audience the videos will most likely reach.
 *
 * The foreign languages are the parent's own list from the child's profile —
 * a deliberate act, like asking for English practice — so they are offered and
 * never preselected.
 */
export const audioLanguageChoices = (
  family: ChildWithProfile[],
  chosenChildIds: ChildId[],
): AudioLanguageChoices => {
  const chosen = family.filter((entry) => chosenChildIds.includes(entry.child.id));
  const audience = chosen.length > 0 ? chosen : family;

  const natives = [...new Set(audience.map((entry) => entry.profile.nativeLanguage))];
  const foreign = audience.flatMap((entry) => entry.profile.foreignLanguages);
  const preferred = natives[0] ?? languageForLocale(DEFAULT_LOCALE);

  return { preferred, options: [...new Set([preferred, ...natives, ...foreign])], natives };
};
